import { useState } from 'react'
import { Pencil, Trash2, Reply, SmilePlus, Download, CheckCheck } from 'lucide-react'
import { AdvisorAvatar } from '../Common/AdvisorAvatar'

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏']

function timeStr(dateStr) {
  return new Date(dateStr).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
}

function groupReactions(reactions) {
  const map = {}
  for (const r of reactions ?? []) {
    if (!map[r.emoji]) map[r.emoji] = { emoji: r.emoji, count: 0, advisorIds: [] }
    map[r.emoji].count++
    map[r.emoji].advisorIds.push(r.advisor_id)
  }
  return Object.values(map)
}

export function DMMessage({
  message, isOwn, isDarkMode, onReply, onEdit, onDelete, onToggleReaction,
  currentAdvisorId, replyToMessage
}) {
  const [hovering, setHovering] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [editText, setEditText] = useState(message.body ?? '')
  const [showEmoji, setShowEmoji] = useState(false)

  const isDeleted = !!message.deleted_at
  const sender = message.sender
  const name = sender?.full_name || sender?.name || 'Unknown'
  const reactionGroups = groupReactions(message.reactions)
  const isRead = (message.reads ?? []).some(r => r.advisor_id !== currentAdvisorId)

  const handleEdit = async () => {
    await onEdit(message.id, editText)
    setEditMode(false)
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this message?')) return
    await onDelete(message.id)
  }

  if (isDeleted) {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} px-4 py-1`}>
        <span className={`text-xs italic px-3 py-2 rounded-lg ${isDarkMode ? 'text-slate-500 bg-slate-800' : 'text-gray-400 bg-gray-100'}`}>
          Message deleted
        </span>
      </div>
    )
  }

  return (
    <div
      className={`flex ${isOwn ? 'flex-row-reverse' : 'flex-row'} items-end gap-2 px-4 py-1 group`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => { setHovering(false); setShowEmoji(false) }}
    >
      {/* Avatar (other side only) */}
      {!isOwn && (
        <AdvisorAvatar avatarUrl={sender?.avatar_url ?? null} fullName={name} size="sm" className="mb-1" />
      )}

      <div className={`max-w-[70%] flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        {/* Sender name */}
        {!isOwn && (
          <span className={`text-xs font-semibold mb-0.5 px-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{name}</span>
        )}

        {/* Reply preview */}
        {replyToMessage && (
          <div className={`text-xs px-3 py-1.5 mb-1 rounded-lg border-l-2 border-sfp-orange max-w-full truncate ${
            isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'
          }`}>
            {replyToMessage.body || `[${replyToMessage.message_type}]`}
          </div>
        )}

        {/* Bubble */}
        <div className={`relative rounded-2xl px-3 py-2 text-sm break-words ${
          isOwn
            ? 'bg-sfp-orange text-white rounded-br-sm'
            : isDarkMode ? 'bg-slate-700 text-slate-100 rounded-bl-sm' : 'bg-gray-100 text-sfp-darkgray rounded-bl-sm'
        }`}>
          {editMode ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleEdit(); if (e.key === 'Escape') setEditMode(false) }}
                className="bg-transparent border-b border-white/50 outline-none text-sm flex-1 min-w-0"
              />
              <button onClick={handleEdit} className="text-xs font-bold opacity-80 hover:opacity-100">Save</button>
            </div>
          ) : (
            <>
              {message.message_type === 'text' && <span>{message.body}</span>}
              {message.message_type === 'image' && message.media_url && (
                <img
                  src={message.media_url}
                  alt={message.media_name || 'image'}
                  className="max-w-[220px] rounded-lg cursor-pointer"
                  onClick={() => window.open(message.media_url, '_blank')}
                />
              )}
              {message.message_type === 'voice' && message.media_url && (
                <audio controls src={message.media_url} className="max-w-[220px]" />
              )}
              {message.message_type === 'file' && message.media_url && (
                <a
                  href={message.media_url}
                  download={message.media_name}
                  className="flex items-center gap-2 text-sm underline"
                >
                  <Download size={14} />
                  {message.media_name || 'Download file'}
                  {message.media_size && (
                    <span className="text-xs opacity-70">({Math.round(message.media_size / 1024)}KB)</span>
                  )}
                </a>
              )}
            </>
          )}
        </div>

        {/* Time + edited + read tick */}
        <div className={`flex items-center gap-1 mt-0.5 px-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
          <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{timeStr(message.created_at)}</span>
          {message.edited_at && <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>· edited</span>}
          {isOwn && isRead && <CheckCheck size={12} className="text-sfp-orange" />}
        </div>

        {/* Reactions */}
        {reactionGroups.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1 px-1">
            {reactionGroups.map(g => {
              const isMine = g.advisorIds.includes(currentAdvisorId)
              return (
                <button
                  key={g.emoji}
                  onClick={() => onToggleReaction(message.id, g.emoji)}
                  className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full border transition ${
                    isMine
                      ? 'border-sfp-orange bg-orange-50 text-sfp-orange'
                      : isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-gray-200 bg-white text-gray-600'
                  }`}
                >
                  {g.emoji} {g.count}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Action toolbar (hover) */}
      {hovering && !isDeleted && !editMode && (
        <div className={`flex items-center gap-1 mb-2 opacity-90 ${isOwn ? 'flex-row-reverse' : ''}`}>
          <div className="relative">
            <button
              onClick={() => setShowEmoji(v => !v)}
              className={`p-1 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-white text-gray-500 hover:bg-gray-100'} shadow`}
            >
              <SmilePlus size={14} />
            </button>
            {showEmoji && (
              <div className={`absolute bottom-8 ${isOwn ? 'right-0' : 'left-0'} flex gap-1 p-2 rounded-xl shadow-lg border z-10 ${
                isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
              }`}>
                {QUICK_EMOJIS.map(e => (
                  <button
                    key={e}
                    onClick={() => { onToggleReaction(message.id, e); setShowEmoji(false) }}
                    className="text-lg hover:scale-125 transition-transform"
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => onReply(message)}
            className={`p-1 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-white text-gray-500 hover:bg-gray-100'} shadow`}
          >
            <Reply size={14} />
          </button>
          {isOwn && (
            <>
              <button
                onClick={() => { setEditMode(true); setEditText(message.body ?? '') }}
                className={`p-1 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-white text-gray-500 hover:bg-gray-100'} shadow`}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={handleDelete}
                className="p-1 rounded-full bg-red-50 text-red-400 hover:bg-red-100 shadow"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
